import EXIF from 'exif-js'
import { promisify } from 'es6-promisify'
import { getCroppedImage } from './image-cropper'

interface NormalizeImageOptions {
  normalizeImageSize?: boolean
  mimeType?: string
  pixelCrop?: any
}

export const exifAsync = promisify(EXIF.getData)

export const getImageSizeData = (
  img: any,
  canvas: HTMLCanvasElement,
  normalizeImageSize: boolean,
  toWidth: number,
  toHeight: number,
) => {
  let isChanged = false
  let resizeWidth = img.width
  let resizeHeight = img.height

  if (normalizeImageSize) {
    if (resizeWidth > toWidth) {
      resizeHeight *= toWidth / resizeWidth
      resizeWidth = toWidth
      isChanged = true
    }

    if (resizeHeight > toHeight) {
      resizeWidth *= toHeight / resizeHeight
      resizeHeight = toHeight
      isChanged = true
    }

    if (isChanged) {
      resizeWidth = Math.floor(resizeWidth)
      resizeHeight = Math.floor(resizeHeight)
      canvas.width = resizeWidth
      canvas.height = resizeHeight
    }
  }
  return {
    resizeWidth,
    resizeHeight,
    isChanged,
  }
}

export const asyncImageLoad = (dataUrl: string) => {
  return new Promise((resolve, reject) => {
    try {
      const img = new Image()
      img.addEventListener('load', () => {
        resolve(img)
      })
      img.src = dataUrl
    } catch (e) {
      reject(e)
    }
  })
}

//options are:
//normalizeImageSize - decrease image size to fit in toWidth and toHeight
export function normalizeImage(
  dataUrl: string,
  toWidth: number,
  toHeight: number,
  {
    normalizeImageSize = false,
    mimeType = 'image/jpeg',
    pixelCrop,
  }: NormalizeImageOptions = {},
): Promise<string> {
  return new Promise(async (resolve, reject) => {
    try {
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      let img: any = await asyncImageLoad(dataUrl)
      await exifAsync(img)

      if (pixelCrop) {
        img = await getCroppedImage(img, pixelCrop)
      }

      // PNGs weigh too much
      if (mimeType === 'image/png') {
        mimeType = 'image/jpeg'
      }

      const { isChanged, resizeWidth, resizeHeight } = getImageSizeData(
        img,
        canvas,
        normalizeImageSize,
        toWidth,
        toHeight,
      )

      if (!isChanged) {
        canvas.width = resizeWidth
        canvas.height = resizeHeight
      }

      if (img.exifdata && img.exifdata.Orientation) {
        // in exif orientations 5,6,7,8 the image width and height flips, so we need to flip
        // the canvas dimensions as well
        if (img.exifdata.Orientation >= 5 && img.exifdata.Orientation <= 8) {
          ;[canvas.width, canvas.height] = [canvas.height, canvas.width]
        }

        switch (img.exifdata.Orientation) {
          case 2:
            ctx.transform(-1, 0, 0, 1, resizeWidth, 0)
            break
          case 3:
            ctx.transform(-1, 0, 0, -1, resizeWidth, resizeHeight)
            break
          case 4:
            ctx.transform(1, 0, 0, -1, 0, resizeHeight)
            break
          case 5:
            ctx.transform(0, 1, 1, 0, 0, 0)
            break
          case 6:
            ctx.transform(0, 1, -1, 0, resizeHeight, 0)
            break
          case 7:
            ctx.transform(0, -1, -1, 0, resizeHeight, resizeWidth)
            break
          case 8:
            ctx.transform(0, -1, 1, 0, 0, resizeWidth)
            break
          default:
            ctx.transform(1, 0, 0, 1, 0, 0)
        }
      }
      ctx.drawImage(img, 0, 0, resizeWidth, resizeHeight)

      return resolve(canvas.toDataURL(mimeType))
    } catch (err) {
      reject(err)
    }
    return resolve(dataUrl)
  })
}

export function base64ToHex(str: string) {
  const raw = atob(str)
  let result = ''
  for (let i = 0; i < raw.length; i++) {
    const hex = raw.charCodeAt(i).toString(16)
    result += hex.length === 2 ? hex : '0' + hex
  }
  return result.toUpperCase()
}
